/**
 * Componenta pentru aprobare plăți Tenant-Instituție
 * Include și funcționalitatea de validare finală lună (smart workflow)
 */
import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Typography,
  TextField,
  Checkbox
} from '@mui/material'
import {
  Check as ApproveIcon,
  Refresh as RefreshIcon,
  Lock as ValidateIcon
} from '@mui/icons-material'
import apiClient, { institutionService } from '../../services/api'
import { TenantBillingEntry, Institution } from '../../types/api'
import toast from 'react-hot-toast'

const TenantBillingApprovalsTab: React.FC = () => {
  const [billingEntries, setBillingEntries] = useState<TenantBillingEntry[]>([])
  const [totalEntries, setTotalEntries] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  
  // Filters
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'draft' | 'approved' | 'validated' | ''>('draft')
  const [monthFilter, setMonthFilter] = useState<number>(new Date().getMonth() + 1)
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear())
  
  // Validare lună dialog
  const [validateDialog, setValidateDialog] = useState(false)
  const [selectedInstitution, setSelectedInstitution] = useState('')
  const [validationNotes, setValidationNotes] = useState('')
  const [validating, setValidating] = useState(false)
  
  // Selected entries pentru bulk approve
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set())
  const [processing, setProcessing] = useState(false)
  
  // Dropdown data
  const [institutions, setInstitutions] = useState<Institution[]>([])

  // Load billing entries
  const loadBillingEntries = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (institutionFilter) params.append('institution_id', institutionFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (monthFilter) params.append('month', String(monthFilter))
      if (yearFilter) params.append('year', String(yearFilter))
      params.append('page', String(page + 1))
      params.append('size', String(rowsPerPage))
      
      const response = await apiClient.get(`/tenant-billing/entries?${params.toString()}`)
      setBillingEntries(response.data.entries)
      setTotalEntries(response.data.total)
    } catch (error: any) {
      console.error('Error loading tenant billing entries:', error)
      toast.error('Eroare la încărcarea plăților instituții')
    } finally {
      setLoading(false)
    }
  }

  // Load institutions
  const loadInstitutions = async () => {
    try {
      const response = await institutionService.getInstitutions({ limit: 200 })
      setInstitutions(response.institutions)
    } catch (error) {
      console.error('Error loading institutions:', error)
      toast.error('Eroare la încărcarea instituțiilor')
    }
  }

  useEffect(() => {
    loadBillingEntries()
  }, [page, rowsPerPage, institutionFilter, statusFilter, monthFilter, yearFilter])

  useEffect(() => {
    loadInstitutions()
  }, [])

  // Aprobare individual
  const handleApprove = async (entryId: string) => {
    try {
      setProcessing(true)
      await apiClient.patch(`/tenant-billing/entries/${entryId}/approve`)
      toast.success('Plată aprobată cu succes')
      loadBillingEntries()
      setSelectedEntries(new Set())
    } catch (error: any) {
      console.error('Error approving entry:', error)
      toast.error(error.response?.data?.detail || 'Eroare la aprobarea plății')
    } finally {
      setProcessing(false)
    }
  }

  // Aprobare bulk
  const handleApproveBulk = async () => {
    if (selectedEntries.size === 0) {
      toast.error('Selectați cel puțin o plată')
      return
    }

    try {
      setProcessing(true)
      await apiClient.post('/tenant-billing/entries/approve-bulk', {
        entry_ids: Array.from(selectedEntries)
      })
      toast.success(`${selectedEntries.size} plăți aprobate cu succes`)
      loadBillingEntries()
      setSelectedEntries(new Set())
    } catch (error: any) {
      console.error('Error approving bulk:', error)
      toast.error(error.response?.data?.detail || 'Eroare la aprobarea plăților')
    } finally {
      setProcessing(false)
    }
  }

  // Validare lună (Smart Workflow)
  const handleOpenValidateDialog = () => {
    if (!institutionFilter) {
      toast.error('Selectați mai întâi o instituție din filtru')
      return
    }
    
    // Verifică că toate entries sunt aprobate
    const hasUnapproved = billingEntries.some(e => e.status === 'draft')
    if (hasUnapproved) {
      toast.error('Toate plățile trebuie să fie aprobate înainte de validare')
      return
    }

    setSelectedInstitution(institutionFilter)
    setValidationNotes('')
    setValidateDialog(true)
  }

  const handleValidateMonth = async () => {
    if (!selectedInstitution) {
      toast.error('Selectați o instituție')
      return
    }

    try {
      setValidating(true)
      const response = await apiClient.post('/tenant-billing/validate-month', {
        institution_id: selectedInstitution,
        month: monthFilter,
        year: yearFilter,
        notes: validationNotes || undefined
      })

      const data = response.data
      toast.success(
        `Luna ${yearFilter}-${monthFilter.toString().padStart(2, '0')} validată!\n` +
        `Tenant: ${data.tenant_entries_validated} entries, ${data.total_tenant_amount} RON\n` +
        `Medici: ${data.medic_entries_validated} entries (validat automat)`
      )
      
      setValidateDialog(false)
      loadBillingEntries()
    } catch (error: any) {
      console.error('Error validating month:', error)
      toast.error(error.response?.data?.detail || 'Eroare la validarea lunii')
    } finally {
      setValidating(false)
    }
  }

  // Toggle selection
  const handleToggleSelect = (entryId: string) => {
    const newSelected = new Set(selectedEntries)
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId)
    } else {
      newSelected.add(entryId)
    }
    setSelectedEntries(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedEntries.size === billingEntries.length) {
      setSelectedEntries(new Set())
    } else {
      setSelectedEntries(new Set(billingEntries.map(e => e.id)))
    }
  }

  // Check if can validate month
  const canValidateMonth = institutionFilter && 
    billingEntries.length > 0 && 
    billingEntries.every(e => e.status === 'approved')

  return (
    <Box>
      {/* Info Alert - visible with proper contrast */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 1.5, 
          py: 0.5,
          bgcolor: 'info.dark', 
          color: 'info.contrastText',
          '& .MuiAlert-icon': { color: 'info.contrastText' },
          '& .MuiTypography-root': { fontSize: '0.78rem' }
        }}
      >
        <strong>Flux Validare:</strong> Aprobă plățile → La sfârșit de lună: "Validare Finală Lună" validează AUTOMAT și plățile medicilor.
      </Alert>

      {/* Filters - compact inline */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }} required>
            <InputLabel>Instituție</InputLabel>
            <Select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              label="Instituție"
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Toate</MenuItem>
              {institutions.map((inst) => (
                <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.75rem' }}>
                  {inst.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Toate</MenuItem>
              <MenuItem value="draft" sx={{ fontSize: '0.75rem' }}>Draft</MenuItem>
              <MenuItem value="approved" sx={{ fontSize: '0.75rem' }}>Aprobat</MenuItem>
              <MenuItem value="validated" sx={{ fontSize: '0.75rem' }}>Validat</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="number"
            label="Luna"
            value={monthFilter}
            onChange={(e) => setMonthFilter(parseInt(e.target.value))}
            inputProps={{ min: 1, max: 12 }}
            sx={{ width: 70, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
          />

          <TextField
            size="small"
            type="number"
            label="Anul"
            value={yearFilter}
            onChange={(e) => setYearFilter(parseInt(e.target.value))}
            sx={{ width: 70, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
          />

          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={loadBillingEntries}
            disabled={loading}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}
          >
            Reîncarcă
          </Button>
        </Box>
      </Paper>

      {/* Actions - compact */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ApproveIcon sx={{ fontSize: 16 }} />}
          onClick={handleApproveBulk}
          disabled={processing || selectedEntries.size === 0}
          sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
        >
          Aprobare ({selectedEntries.size})
        </Button>

        {canValidateMonth && (
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<ValidateIcon sx={{ fontSize: 16 }} />}
            onClick={handleOpenValidateDialog}
            sx={{ ml: 'auto', height: 28, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Validare Lună {yearFilter}-{monthFilter.toString().padStart(2, '0')}
          </Button>
        )}
      </Box>

      {/* Table - compact with dark header */}
      <TableContainer component={Paper} sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiChip-root': { height: 20, fontSize: '0.75rem' },
        '& .MuiTableCell-root': { py: 0.5, px: 1 },
        '& .MuiTableBody-root .MuiIconButton-root': { p: '3px' },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              bgcolor: 'grey.900',
              '& th': {
                color: 'common.white',
                fontSize: '0.8rem',
                fontWeight: 700,
                py: 1,
                px: 1.5,
                whiteSpace: 'nowrap'
              }
            }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={billingEntries.length > 0 && selectedEntries.size === billingEntries.length}
                  indeterminate={selectedEntries.size > 0 && selectedEntries.size < billingEntries.length}
                  onChange={handleSelectAll}
                  sx={{ color: 'common.white', '&.Mui-checked': { color: 'common.white' } }}
                />
              </TableCell>
              <TableCell>Instituție</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>Ore</TableCell>
              <TableCell>Tarif/Oră</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Lună</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">Se încarcă...</TableCell>
              </TableRow>
            ) : billingEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Nu există plăți pentru filtrele selectate
                </TableCell>
              </TableRow>
            ) : (
              billingEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedEntries.has(entry.id)}
                      onChange={() => handleToggleSelect(entry.id)}
                      disabled={entry.status !== 'draft'}
                    />
                  </TableCell>
                  <TableCell>{entry.institution_name}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{entry.description}</Typography>
                  </TableCell>
                  <TableCell>{entry.hours_worked} h</TableCell>
                  <TableCell>{entry.hourly_rate} {entry.currency}</TableCell>
                  <TableCell>
                    <strong>{entry.total_amount} {entry.currency}</strong>
                  </TableCell>
                  <TableCell>
                    {entry.billing_year}-{entry.billing_month.toString().padStart(2, '0')}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        entry.status === 'draft' ? 'Draft' :
                        entry.status === 'approved' ? 'Aprobat' : 'Validat'
                      }
                      color={
                        entry.status === 'draft' ? 'default' :
                        entry.status === 'approved' ? 'primary' : 'success'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {entry.status === 'draft' && (
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleApprove(entry.id)}
                        disabled={processing}
                      >
                        <ApproveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalEntries}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          labelRowsPerPage="Rânduri per pagină:"
        />
      </TableContainer>

      {/* Dialog Validare Lună */}
      <Dialog open={validateDialog} onClose={() => setValidateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Validare Finală Lună {yearFilter}-{monthFilter.toString().padStart(2, '0')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Atenție!</strong> Această acțiune va:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                <li>Valida toate plățile tenant → instituție pentru luna selectată</li>
                <li><strong>Valida AUTOMAT</strong> toate plățile medici → aceeași instituție</li>
                <li>Închide luna (nu se vor mai putea face modificări)</li>
                <li>Genera anexă pentru plată (viitor)</li>
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Note (opțional)"
              multiline
              rows={3}
              value={validationNotes}
              onChange={(e) => setValidationNotes(e.target.value)}
              placeholder="Ex: Validat conform anexei de plată nr. 123"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateDialog(false)} disabled={validating}>
            Anulează
          </Button>
          <Button
            onClick={handleValidateMonth}
            variant="contained"
            color="success"
            disabled={validating}
            startIcon={<ValidateIcon />}
          >
            {validating ? 'Se validează...' : 'Confirmă Validare Lună'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TenantBillingApprovalsTab
